import { useEffect, useState } from "react";
import { Title, useNotify, usePermissions } from "react-admin";
import {
  Box,
  Button,
  Card,
  Divider,
  Drawer,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";

import { listOrders, getOrder, updateOrderStatus } from "../../utils/salatMahsoolApi";
import { OrderStatusChip, canWrite } from "./common";

const STATUS_OPTIONS = ["Pending", "Confirmed", "Processing", "Ready", "Delivered", "Cancelled"];

const formatAmount = (amount) => {
  const n = parseFloat(amount);
  return isNaN(n) ? amount : n.toLocaleString(undefined, { style: "currency", currency: "SDG" });
};

const OrderDetailDrawer = ({ orderId, open, onClose, onSaved, canEdit }) => {
  const notify = useNotify();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    getOrder(orderId)
      .then((o) => {
        setOrder(o);
        setStatus(o.status || "");
        setAdminNotes(o.admin_notes || "");
      })
      .catch((err) => notify(`Error: ${err.message}`, { type: "error" }))
      .finally(() => setLoading(false));
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateOrderStatus(orderId, { status, admin_notes: adminNotes });
      notify("Order updated", { type: "success" });
      onSaved();
      onClose();
    } catch (err) {
      notify(`Error: ${err.message}`, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 480, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Order #{orderId}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {loading || !order ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Buyer
            </Typography>
            <Typography variant="body2">{order.buyer?.name}</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {order.buyer?.phonenumber}
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
              Delivery Address
            </Typography>
            <Typography variant="body2">
              {[order._state?.name, order._city?.name, order._locality?.name]
                .filter(Boolean)
                .join(" / ") || "-"}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {order.address || "-"}
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
              Items
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(order.items || []).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell align="right">{formatAmount(item.unit_price)}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{formatAmount(item.subtotal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="subtitle1" align="right" sx={{ mt: 1 }}>
              Total: {formatAmount(order.total_amount)}
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
              Buyer Notes
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {order.notes || "-"}
            </Typography>

            <TextField
              label="Admin Notes"
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              value={adminNotes}
              disabled={!canEdit}
              onChange={(e) => setAdminNotes(e.target.value)}
            />

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={status}
                disabled={!canEdit}
                onChange={(e) => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {canEdit && (
              <Box display="flex" justifyContent="flex-end" gap={1} mt={3}>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  Save
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

const SalatOrdersPage = () => {
  const notify = useNotify();
  const { permissions } = usePermissions();
  const canEdit = canWrite(permissions);

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [statusFilter, setStatusFilter] = useState("");
  const [buyerId, setBuyerId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const refetch = () => {
    setLoading(true);
    listOrders({
      page: page + 1,
      limit,
      status: statusFilter || undefined,
      buyer_id: buyerId || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    })
      .then((res) => {
        setOrders(res.data || []);
        setTotal(res.total || 0);
      })
      .catch((err) => notify(`Error: ${err.message}`, { type: "error" }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, statusFilter, buyerId, fromDate, toDate]);

  return (
    <Card sx={{ m: 2, p: 2 }}>
      <Title title="Salat Mahsool - Orders" />
      <Typography variant="h5" mb={2}>
        Salat Mahsool Orders
      </Typography>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Buyer ID"
            size="small"
            fullWidth
            value={buyerId}
            onChange={(e) => {
              setBuyerId(e.target.value);
              setPage(0);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="From Date"
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setPage(0);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="To Date"
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setPage(0);
            }}
          />
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Buyer</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} hover onClick={() => setSelectedOrderId(o.id)} sx={{ cursor: "pointer" }}>
                  <TableCell>#{o.id}</TableCell>
                  <TableCell>
                    {o.buyer?.name}
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      {o.buyer?.phonenumber}
                    </Typography>
                  </TableCell>
                  <TableCell>{o.items?.length ?? 0}</TableCell>
                  <TableCell>{formatAmount(o.total_amount)}</TableCell>
                  <TableCell>
                    <OrderStatusChip status={o.status} />
                  </TableCell>
                  <TableCell>{o.created_at ? new Date(o.created_at).toLocaleDateString() : "-"}</TableCell>
                </TableRow>
              ))}
              {orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No orders found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => {
              setLimit(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}

      <OrderDetailDrawer
        orderId={selectedOrderId}
        open={!!selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
        onSaved={refetch}
        canEdit={canEdit}
      />
    </Card>
  );
};

export default SalatOrdersPage;
